import React, { useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';

interface Domain {
  id: number;
  domain_name: string;
}

interface EmailData {
  email: string;
}

interface EmailsProps {
  selectedDomain: string | null;
  emailsData: EmailData[];
  domains: Domain[];
  onDomainChange: (domain: string) => void;
}

const Emails: React.FC<EmailsProps> = ({
  selectedDomain,
  emailsData,
  domains,
  onDomainChange,
}) => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const dataPerPage = 10;

  // Cambio de dominio => reinicio de paginación
  const handleDomainChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onDomainChange(e.target.value);
    setCurrentPage(1);
  };

  // Ordenar emails
  const sortEmails = (emails: EmailData[], order: 'asc' | 'desc') => {
    return [...emails].sort((a, b) => {
      const emailA = a.email.toLowerCase();
      const emailB = b.email.toLowerCase();
      if (order === 'asc') return emailA < emailB ? -1 : emailA > emailB ? 1 : 0;
      return emailA > emailB ? -1 : emailA < emailB ? 1 : 0;
    });
  };

  const sortedEmails = sortEmails(emailsData, sortOrder);

  // Paginación
  const totalItems = sortedEmails.length;
  const totalPages = Math.ceil(totalItems / dataPerPage) || 1;
  const indexOfLastData = currentPage * dataPerPage;
  const indexOfFirstData = indexOfLastData - dataPerPage;
  const currentData = sortedEmails.slice(indexOfFirstData, indexOfLastData);

  // Cambios de página
  const goToNextPage = () =>
    setCurrentPage((prev) => (prev < totalPages ? prev + 1 : prev));
  const goToPreviousPage = () =>
    setCurrentPage((prev) => (prev > 1 ? prev - 1 : prev));

  // Toggle asc/desc
  const toggleSortOrder = () => {
    setSortOrder((prevOrder) => (prevOrder === 'asc' ? 'desc' : 'asc'));
  };

  return (
    <div className="flex space-x-6">
      {/* Tabla de correos electrónicos */}
      <div className="w-1/2">
        <div className="bg-white p-6 rounded-lg shadow-lg mb-6">
          <div className="flex items-center mb-6">
            <div className="w-3 h-10 bg-gray-800 mr-4"></div>
            <h3 className="text-4xl font-bold">{t('emails.title')}</h3>
          </div>

          {/* Select de dominio */}
          <div className="mb-4">
            <label
              htmlFor="domain-select"
              className="block text-2xl font-medium text-gray-700 mb-4"
            >
              {t('emails.select_domain')}
            </label>
            <select
              id="domain-select"
              value={selectedDomain || ''}
              onChange={handleDomainChange}
              className="block w-full max-w-md mt-1 p-2 border border-gray-800 bg-gray-800 text-white font-bold rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            >
              {domains.map((domain) => (
                <option key={domain.id} value={domain.domain_name}>
                  {domain.domain_name}
                </option>
              ))}
            </select>
          </div>

          {currentData.length > 0 ? (
            <>
              <div className="min-h-[350px]" style={{ minHeight: '550px' }}>
                <table className="min-w-full bg-white table-auto">
                  <thead>
                    <tr className="bg-gray-800 text-white">
                      <th
                        className="py-3 px-6 text-left text-sm font-semibold tracking-wider cursor-pointer"
                        onClick={toggleSortOrder}
                      >
                        {t('emails.email_column')} {sortOrder === 'asc' ? '▲' : '▼'}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentData.map((email, index) => (
                      <tr
                        key={index}
                        className="text-gray-700 bg-gray-50 even:bg-gray-100 hover:bg-gray-200"
                      >
                        <td className="py-3 px-6 border">{email.email}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Paginación */}
              {totalItems > dataPerPage && (
                <div className="mt-6 flex justify-center items-center space-x-4">
                  <button
                    onClick={goToPreviousPage}
                    disabled={currentPage === 1}
                    className="px-4 py-2 bg-gray-800 text-white rounded disabled:bg-gray-300 disabled:text-gray-600"
                  >
                    {t('emails.pagination.previous')}
                  </button>

                  <span>
                    {t('emails.pagination.info', {
                      current: currentPage,
                      total: totalPages,
                    })}
                  </span>

                  <button
                    onClick={goToNextPage}
                    disabled={currentPage === totalPages}
                    className="px-4 py-2 bg-gray-800 text-white rounded disabled:bg-gray-300 disabled:text-gray-600"
                  >
                    {t('emails.pagination.next')}
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center my-8">
              <h3 className="text-2xl font-bold">
                {t('emails.no_emails')}
              </h3>
            </div>
          )}
        </div>
      </div>

      {/* Sección de información a la derecha */}
      <div className="w-1/2 bg-white p-6 rounded-lg shadow-lg" style={{ height: '100%' }}>
        <div className="flex items-center mb-6">
          <div className="w-3 h-10 bg-gray-800 mr-4"></div>
          <h3 className="text-3xl font-bold">{t('emails.info_title')}</h3>
        </div>
        <p className="text-gray-700 mb-4 text-xl">
          <Trans i18nKey="emails.info_paragraph1">
          </Trans>
        </p>
        <p className="text-gray-700 mb-4 text-xl">
          <Trans i18nKey="emails.info_paragraph2">
          </Trans>
        </p>
        <p className="text-gray-700 text-xl">
          <Trans i18nKey="emails.info_paragraph3">
          </Trans>
        </p>
      </div>
    </div>
  );
};

export default Emails;
